/**
 * Internal Invoices Redux Slice (Cabeceras)
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface InternalInvoice {
  id: number;
  booking_id: number | null;
  issued_at: string | null;
  due_date: string | null;
  amount_gross: number;
  amount_net: number;
  tax_amount: number;
  tax_rate: number | null;
  status_id: number | null;
  comments: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
interface InternalInvoicesState extends FilteredState<InternalInvoice> {
  selectedInvoice: InternalInvoice | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}
const initialState: InternalInvoicesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'issued_at',
  sortDirection: 'desc',
  searchTerm: null,
  selectedInvoice: null,
  creating: false,
  updating: false,
  deleting: false,
};
export const fetchInternalInvoices = createAsyncThunk<{ data: InternalInvoice[]; totalCount: number }, any>(
  'internalInvoices/fetchInvoices',
  async (params: any) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchInternalInvoiceById = createAsyncThunk('internalInvoices/fetchInvoiceById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createInternalInvoice = createAsyncThunk('internalInvoices/createInvoice', async (invoice: Partial<InternalInvoice>) => {
  throw new Error('Not implemented yet');
});
export const updateInternalInvoice = createAsyncThunk('internalInvoices/updateInvoice', async ({ id, data }: { id: number; data: Partial<InternalInvoice> }) => {
  throw new Error('Not implemented yet');
});
export const deleteInternalInvoice = createAsyncThunk('internalInvoices/deleteInvoice', async (id: number) => {
  throw new Error('Not implemented yet');
});
const internalInvoicesSlice = createSlice({
  name: 'internalInvoices',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectInvoice: (state, action: PayloadAction<InternalInvoice | null>) => {
      state.selectedInvoice = action.payload;
    },
    clearSelection: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInternalInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInternalInvoices.fulfilled, (state, action: PayloadAction<{ data: InternalInvoice[]; totalCount: number }>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchInternalInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar facturas internas';
      });
  },
});
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectInvoice, clearSelection } = internalInvoicesSlice.actions;
export default internalInvoicesSlice.reducer;
